<template>
  <AdminAssetTable
    :config="usersConfig"
    :items="users"
    :users="allUsers"
    :api-service="usersApiService"
    @refresh="loadUsers"
    @created="handleUserCreated"
    @updated="handleUserUpdated"
    @deleted="handleUserDeleted"
  />
</template>

<script setup lang="ts">
import type { Entity } from '@/api/types/core/common';
import type { User } from '@/api/types/core/users';
import AdminAssetTable from '@/components/admin/AdminAssetTable.vue';
import { ADMIN_ASSET_CONFIGS } from '@/configs/adminAssetConfigs';
import { secureApiServices } from '@/services/secureApiService';
import { onMounted, ref } from 'vue';

// Fonction utilitaire pour générer un mot de passe par défaut
const generateDefaultPassword = (): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
  let password = '';
  for (let i = 0; i < 12; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
};

// Configuration
const usersConfig = ADMIN_ASSET_CONFIGS.users;

// État
const users = ref<Entity[]>([]);
const allUsers = ref<User[]>([]);
const loading = ref(false);

// Service API adapté pour AdminAssetTable
const usersApiService = {
  async getAll(): Promise<Entity[]> {
    const data = await secureApiServices.users.getAll();
    // Conversion des User vers Entity en ajoutant les champs manquants
    return data.map((user: Entity): Entity => ({
      ...user,
      user_id: (user as unknown as User).uuid, // Pour les utilisateurs, user_id = uuid
      is_deleted: false   // Les utilisateurs ne sont jamais supprimés logiquement
    }));
  },
  
  async create(data: Record<string, unknown>): Promise<Entity> {
    // Générer un mot de passe par défaut si absent
    const password = data.password as string || generateDefaultPassword();
    
    const userData: Record<string, unknown> = {
      name: data.name as string,
      email: data.email as string,
      password: password,
      role: data.role as string || 'user',
      fonction: data.fonction as string || '',
      affiliation: data.affiliation as string || '',
      is_public: data.is_public as boolean || true
    };
    
    // Conserver l'UUID s'il est fourni dans les données d'import
    if (data.uuid && typeof data.uuid === 'string' && data.uuid.trim()) {
      userData.uuid = data.uuid;
      console.log(`[USERS IMPORT] Création utilisateur avec UUID conservé: ${data.uuid} - ${userData.email}`);
    } else {
      console.log(`[USERS IMPORT] Création utilisateur avec nouvel UUID: ${userData.email}`);
    }
    
    const result = await secureApiServices.users.create(userData);
    return {
      ...result,
      user_id: result.uuid,
      is_deleted: false
    };
  },
  
  async update(uuid: string, data: Record<string, unknown>): Promise<Entity> {
    const result = await secureApiServices.users.update(uuid, data);
    return {
      ...result,
      user_id: result.uuid,
      is_deleted: false
    };
  },
  
  async delete(uuid: string): Promise<void> {
    await secureApiServices.users.delete(uuid);
  }
};

// Méthodes
const loadUsers = async (): Promise<void> => {
  loading.value = true;
  try {
    const [usersData, allUsersData] = await Promise.all([
      usersApiService.getAll(),
      secureApiServices.users.getAll()
    ]);
    users.value = usersData;
    allUsers.value = allUsersData as unknown as User[];
  } catch (error) {
    console.error('Erreur lors du chargement des utilisateurs:', error);
  } finally {
    loading.value = false;
  }
};

// Gestionnaires d'événements
const handleUserCreated = (user: Entity): void => {
  console.log('Utilisateur créé:', user);
  // L'actualisation est gérée par l'événement refresh
};

const handleUserUpdated = (user: Entity): void => {
  console.log('Utilisateur mis à jour:', user);
  // L'actualisation est gérée par l'événement refresh
};

const handleUserDeleted = (uuid: string): void => {
  console.log('Utilisateur supprimé:', uuid);
  // L'actualisation est gérée par l'événement refresh
};

// Lifecycle
onMounted(async (): Promise<void> => {
  await loadUsers();
});
</script>


  